import { Drawer, Form, Button, Spin, Card } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import { get as _get } from 'lodash';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import React from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../../../utils/ReduxUtils';
import { getSelectedItems } from '../../../selectors/catalogue/catalogueSelector';
import FormCheckboxGroup, { getCheckable } from '../../global/Forms/FormCheckboxGroup';
import { brandAccDefaultValues } from '../../../selectors/default_values/defaultValuesSelector';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import catalougeActions from '../../../actions/catalogue/catalogue';
import { ThunkAsyncDispatch } from '../../../../types/global';
import { DefaultValue } from '../../../../types/brand_settings';
import { fetchMappedSegments } from '../../../actions/catalogue/createItem/fetch';
import { ApplicationState } from '../../../reducers';

const DRAWER_WIDTH = 400;
const RESOURCE_TABLE_LANGUAGES = 'languages';

type FormValues = {
  partNumber: string;
  subBrand?: number;
  segments: string[];
};

type CatalogueCopyProductDrawerProps = {
  visible: boolean;
  onClose: () => void;
};

const CatalogueCopyProductDrawer: React.FC<CatalogueCopyProductDrawerProps> = props => {
  const { t } = useTranslation();
  const dispatch: ThunkAsyncDispatch = useDispatch();

  const mapSegments = (segments: Record<string, boolean>) =>
    Object.entries(segments).map(([id, enabled]) => ({
      id,
      disabled: !enabled,
      name: t(`catalogue:copyDrawer.segments.${id}`),
    }));

  const {
    brandId,
    subBrands,
    fetchingMappedSegments,
    rawSegments,
    selectedItems,
    selectedFilterGo,
    catalogueQueryParams,
    defaultValues,
  } = useSelector((state: ApplicationState) => {
    const selectedItems = getSelectedItems(state);
    const rawSegments = state.catalogue.createItem.mappedSegments;

    return {
      brandId: state.parent.brands.selectedBrandId,
      subBrands: state.brand.currentBrand.subBrands,
      fetchingMappedSegments: state.catalogue.createItem.fetchingMappedSegments,
      rawSegments,
      selectedItems,
      selectedFilterGo: state.catalogue.filter.filterGo,
      catalogueQueryParams: state.catalogue.catalogue.catalogueQueryParams,
      defaultValues: brandAccDefaultValues(state),
    };
  }, shallowEqual);

  const mappedSegments = React.useMemo(() => mapSegments(rawSegments || {}), [rawSegments]);

  const checkAbleSegments = React.useMemo(() => getCheckable(mappedSegments), [mappedSegments]);

  const formMethods = useForm({
    defaultValues: {
      partNumber: '',
      subBrand: undefined,
      segments: [] as string[],
    },
    resolver: yupResolver(
      Yup.object().shape({
        partNumber: Yup.string().required(t('validation:required')),
        subBrand: Yup.number().optional(),
        segments: Yup.array().of(Yup.string().required()).required(),
      })
    ),
    mode: 'onTouched',
  });

  const { handleSubmit, formState, reset } = formMethods;
  const { isSubmitting } = formState;

  const selectedItemId = selectedItems[0]?.id;

  React.useEffect(() => {
    if (selectedItemId) {
      dispatch(fetchMappedSegments(selectedItemId));
    }
  }, [dispatch, selectedItemId]);

  React.useEffect(() => {
    if (checkAbleSegments.length > 0) {
      reset({
        partNumber: '',
        subBrand: undefined,
        segments: checkAbleSegments,
      });
    }
  }, [checkAbleSegments, reset]);

  if (!props.visible || !selectedItems?.[0] || !selectedFilterGo) {
    return null;
  }

  const handleClose = () => {
    reset();
    props.onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const { partNumber, subBrand, segments } = values;
    try {
      await dispatch(
        catalougeActions.cloneItem({
          itemId: selectedItems[0].id,
          partNumber,
          brandId,
          subBrandId: subBrand,
          segments,
          currentFilterId: selectedFilterGo.id,
          selectedReceiverId: catalogueQueryParams.selectedReceiverId,
          selectedChannelId: catalogueQueryParams.selectedChannelId,
          languageId: defaultValues.find(
            (d: DefaultValue) => d.resource_table === RESOURCE_TABLE_LANGUAGES
          )?.value,
          order: catalogueQueryParams.order,
        })
      );
      handleClose();
    } catch (e) {
      const message = _get(e, 'response.data.message');
      const status = _get(e, 'response.status');
      if (message && status === 422) {
        formMethods.setError('partNumber', { type: 'manual', message });
      }
    }
  };

  const itemToCopy = selectedItems[0];
  return (
    <Drawer
      title={t('catalogue:copyDrawer.cloneProduct')}
      width={DRAWER_WIDTH}
      open={props.visible}
      onClose={handleClose}
    >
      {itemToCopy && (
        <Card title={t('catalogue:copyDrawer.originalProduct')}>
          <div>
            <div>
              <span>{`${t('catalogue:copyDrawer.partNumber')}:`}</span>
              <br />
              <span>{itemToCopy.part_number}</span>
            </div>
            <br />
            <div>
              <span>{`${t('catalogue:copyDrawer.previewName')}:`}</span>
              <br />
              <span>{itemToCopy.short_name}</span>
            </div>
          </div>
        </Card>
      )}

      {fetchingMappedSegments === LoadingState.LOADING_DONE ? (
        <FormProvider {...formMethods}>
          <Form layout="vertical" className="copy-drawer-form" onFinish={handleSubmit(onSubmit)}>
            <FormInput
              name="partNumber"
              label={t('catalogue:copyDrawer.partNumber')}
              placeholder={t('catalogue:copyDrawer.placeholderPartNumber')}
              required
              useHookForm
            />
            <FormSelect
              name="subBrand"
              label={t('catalogue:copyDrawer.subBrand')}
              placeholder={t('catalogue:copyDrawer.placeholderSubBrand')}
              values={subBrands}
              allowClear
              useHookForm
            />
            <FormCheckboxGroup name="segments" elements={mappedSegments} useHookForm />
            <Button
              className="copy-drawer-form__submit"
              disabled={isSubmitting}
              type="primary"
              size="small"
              htmlType="submit"
            >
              {t('catalogue:copyDrawer.submit')}
            </Button>
          </Form>
        </FormProvider>
      ) : (
        <Spin />
      )}
    </Drawer>
  );
};

export default CatalogueCopyProductDrawer;
